import React, { useState, useRef, useLayoutEffect, useEffect, useCallback, useId } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../../lib/utils'

export interface TooltipProps {
  content: string
  side?: 'top' | 'bottom' | 'left' | 'right'
  align?: 'start' | 'center' | 'end'
  className?: string
  children: React.ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
  disabled?: boolean
  maxWidthPx?: number
}

// Internal helper to throttle a function with rAF
function useRafCallback<T extends (...args: unknown[]) => void>(fn: T) {
  const fnRef = useRef(fn)
  fnRef.current = fn
  const frame = useRef<number | undefined>(undefined)
  return useCallback<(...args: Parameters<T>) => void>((...args) => {
    if (frame.current) cancelAnimationFrame(frame.current)
    frame.current = requestAnimationFrame(() => fnRef.current(...args))
  }, [])
}

const Tooltip: React.FC<TooltipProps> = ({
  content,
  side = 'top',
  align = 'center',
  className,
  children,
  open,
  onOpenChange,
  disabled = false,
  maxWidthPx = 280,
}) => {
  const isControlled = open !== undefined
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const isVisible = isControlled ? !!open : uncontrolledOpen

  const ENTER_DELAY = 240
  const LEAVE_DELAY = 80
  const TRANSITION_MS = 140
  const [mounted, setMounted] = useState(false) // mounted in DOM for measurement & animation
  const [positioned, setPositioned] = useState(false) // position calculated at least once
  const unmountTimer = useRef<number | null>(null)

  const [position, setPosition] = useState<{ x: number; y: number; side: string }>({ x: -9999, y: -9999, side })
  const triggerRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const showTimer = useRef<number | null>(null)
  const hideTimer = useRef<number | null>(null)
  const id = useId()

  const setOpen = useCallback((value: boolean) => {
    if (disabled) return
    if (value) {
      if (!mounted) setMounted(true)
      // reset positioning to force recompute cleanly
      setPositioned(false)
    }
    if (isControlled) {
      onOpenChange?.(value)
    } else {
      if (!value) {
        // start exit animation then unmount
        clearTimers()
        setUncontrolledOpen(false)
        if (unmountTimer.current) clearTimeout(unmountTimer.current)
        unmountTimer.current = window.setTimeout(() => {
          setMounted(false)
          setPositioned(false)
        }, TRANSITION_MS + 40)
      } else {
        setUncontrolledOpen(true)
      }
    }
    if (!value && isControlled) {
      // controlled close -> schedule unmount unless parent keeps open true again quickly
      if (unmountTimer.current) clearTimeout(unmountTimer.current)
      unmountTimer.current = window.setTimeout(() => {
        if (!open) {
          setMounted(false)
          setPositioned(false)
        }
      }, TRANSITION_MS + 40)
    }
  }, [disabled, isControlled, onOpenChange, mounted, open])

  const clearTimers = () => {
    if (showTimer.current) {
      clearTimeout(showTimer.current)
      showTimer.current = null
    }
    if (hideTimer.current) {
      clearTimeout(hideTimer.current)
      hideTimer.current = null
    }
  }

  const scheduleShow = () => {
    if (disabled) return
    clearTimers()
    showTimer.current = window.setTimeout(() => setOpen(true), ENTER_DELAY)
  }

  const scheduleHide = () => {
    if (disabled) return
    clearTimers()
    hideTimer.current = window.setTimeout(() => setOpen(false), LEAVE_DELAY)
  }

  const forceHideImmediate = useCallback(() => {
    clearTimers()
    setOpen(false)
  }, [setOpen])

  const computePosition = useCallback(() => {
    if (!triggerRef.current || !tooltipRef.current) return
    const triggerRect = triggerRef.current.getBoundingClientRect()
    const tooltipRect = tooltipRef.current.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    let desiredSide: TooltipProps['side'] = side
    let x: number
    let y: number

    const GAP = 10

    const base = (s: typeof desiredSide) => {
      switch (s) {
        case 'top':
          return {
            x: triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2,
            y: triggerRect.top - tooltipRect.height - GAP,
          }
        case 'bottom':
          return {
            x: triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2,
            y: triggerRect.bottom + GAP,
          }
        case 'left':
          return {
            x: triggerRect.left - tooltipRect.width - GAP,
            y: triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2,
          }
        case 'right':
        default:
          return {
            x: triggerRect.right + GAP,
            y: triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2,
          }
      }
    }

    // Initial
    ;({ x, y } = base(desiredSide))

    // Flip logic
    const fitsTop = triggerRect.top >= tooltipRect.height + GAP + 4
    const fitsBottom = viewportHeight - triggerRect.bottom >= tooltipRect.height + GAP + 4
    const fitsLeft = triggerRect.left >= tooltipRect.width + GAP + 4
    const fitsRight = viewportWidth - triggerRect.right >= tooltipRect.width + GAP + 4

    if (desiredSide === 'top' && !fitsTop && fitsBottom) desiredSide = 'bottom'
    else if (desiredSide === 'bottom' && !fitsBottom && fitsTop) desiredSide = 'top'
    else if (desiredSide === 'left' && !fitsLeft && fitsRight) desiredSide = 'right'
    else if (desiredSide === 'right' && !fitsRight && fitsLeft) desiredSide = 'left'

    ;({ x, y } = base(desiredSide))

    // Alignment adjustments
    if (desiredSide === 'top' || desiredSide === 'bottom') {
      if (align === 'start') x = triggerRect.left
      else if (align === 'end') x = triggerRect.right - tooltipRect.width
    } else {
      if (align === 'start') y = triggerRect.top
      else if (align === 'end') y = triggerRect.bottom - tooltipRect.height
    }

    // Clamp within viewport (with margin)
    const margin = 8
    x = Math.max(margin, Math.min(x, viewportWidth - tooltipRect.width - margin))
    y = Math.max(margin, Math.min(y, viewportHeight - tooltipRect.height - margin))

    setPosition({ x, y, side: desiredSide })
    setPositioned(true)
  }, [side, align])

  const rafComputePosition = useRafCallback(computePosition)

  // Recompute when open or window events
  useLayoutEffect(() => {
    if (mounted && isVisible && !positioned) {
      // first measurement pass (tooltip rendered but visually hidden & at 0,0)
      computePosition()
    }
  }, [mounted, isVisible, positioned, computePosition])

  useEffect(() => {
    if (!isVisible || !mounted) return
    const handleScroll = () => rafComputePosition()
    const handleResize = () => rafComputePosition()
    window.addEventListener('scroll', handleScroll, true)
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('scroll', handleScroll, true)
      window.removeEventListener('resize', handleResize)
    }
  }, [isVisible, mounted, rafComputePosition])

  // Keyboard dismissal (Escape)
  useEffect(() => {
    if (!isVisible) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') forceHideImmediate()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isVisible, forceHideImmediate])

  // Cleanup timers on unmount
  useEffect(() => () => clearTimers(), [])

  const tooltipContent = mounted && typeof document !== 'undefined' && createPortal(
    <div
      ref={tooltipRef}
      id={id}
      role="tooltip"
      data-side={position.side}
      data-open={isVisible ? 'true' : 'false'}
      data-positioned={positioned ? 'true' : 'false'}
      aria-hidden={!isVisible}
      className={cn(
        'fixed z-[9999] will-change-transform select-none pointer-events-none',
        'rounded-md border border-foreground/10 bg-foreground/90 text-background backdrop-blur-sm',
        'px-3 py-2 text-[13px] leading-tight shadow-lg shadow-foreground/10',
        // Hidden before positioning => no flicker / jump
        !positioned && 'opacity-0 invisible',
        positioned && 'visible',
        // Entrance / exit animation only after positioned
        positioned && 'transition-opacity transition-transform duration-140 ease-out',
        positioned && isVisible && 'opacity-100 scale-100',
        positioned && !isVisible && 'opacity-0 scale-95',
        // Directional subtle translate
        positioned && isVisible && 'data-[side=top]:translate-y-[-2px] data-[side=bottom]:translate-y-[2px] data-[side=left]:translate-x-[-2px] data-[side=right]:translate-x-[2px]',
        positioned && !isVisible && 'data-[side=top]:translate-y-0 data-[side=bottom]:translate-y-0 data-[side=left]:translate-x-0 data-[side=right]:translate-x-0',
        'origin-center',
        className
      )}
      style={{
        left: positioned ? position.x : 0,
        top: positioned ? position.y : 0,
        maxWidth: maxWidthPx,
      }}
    >
      {content}
      <span
        aria-hidden="true"
        className={cn(
          'absolute block w-2.5 h-2.5 rotate-45 border border-foreground/10 bg-foreground/90',
          'data-[side=top]:bottom-[-5px] data-[side=top]:left-1/2 data-[side=top]:-translate-x-1/2',
          'data-[side=bottom]:top-[-5px] data-[side=bottom]:left-1/2 data-[side=bottom]:-translate-x-1/2',
          'data-[side=left]:right-[-5px] data-[side=left]:top-1/2 data-[side=left]:-translate-y-1/2',
          'data-[side=right]:left-[-5px] data-[side=right]:top-1/2 data-[side=right]:-translate-y-1/2'
        )}
        data-side={position.side}
      />
    </div>,
    document.body
  )

  return (
    <>
      <div
        ref={triggerRef}
        aria-describedby={isVisible ? id : undefined}
        onMouseEnter={scheduleShow}
        onMouseLeave={scheduleHide}
        onFocus={scheduleShow}
        onBlur={scheduleHide}
        onClick={forceHideImmediate}
        // Touch: tap & hold feel. Simple approach: show on touchstart, hide on touchend
        onTouchStart={scheduleShow}
        onTouchEnd={scheduleHide}
        className="inline-block cursor-pointer w-full"
      >
        {children}
      </div>
      {tooltipContent}
    </>
  )
}

export { Tooltip }
export default Tooltip